import { type FC, useEffect, useState } from "react";

import type { User } from "../../../../core/types/fe/entities/user";
import type { ExtendedColumnsType } from "../../../index";
import { PropertyTable } from "../../../Table/components/PropertyTable";
import type { AntCustomFormField, CommonFieldAttributes } from "../../types/types";
import { fetchUser } from "./fetchMSEUsers";
import { MSEUserSelector } from "./MSEUserSelector";

const getColumnsConfig = (t: Function): ExtendedColumnsType<User>[] => {
    return [
        {
            title: t("dataGroup.id"),
            key: "userId",
            dataIndex: "userId",
        },
        {
            title: t("dataGroups.createForm.usersTable.firstName"),
            key: "firstName",
            dataIndex: "firstName",
            editable: true,
        },
        {
            title: t("dataGroups.createForm.usersTable.lastName"),
            key: "lastName",
            dataIndex: "lastName",
            editable: true,
        },
        {
            title: t("common.username"),
            key: "username",
            dataIndex: "username",
        },
    ];
};

export interface DataGroupUsersTableProps extends AntCustomFormField<User[]>, CommonFieldAttributes {}

export const DataGroupUsersTable: FC<DataGroupUsersTableProps> = ({ value, onChange }) => {
    const [users, setUsers] = useState<User[]>(value || []);
    const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);

    async function updateUsers(inputUsers: User[]) {
        const updatedUsers = await Promise.all(
            inputUsers.map(async (user) => {
                if (user && user.userId) {
                    try {
                        const fetchedUser = await fetchUser(user.userId);
                        return { ...user, ...fetchedUser };
                    } catch (error) {
                        return user;
                    }
                }
                return user;
            }),
        );
        return updatedUsers;
    }

    useEffect(() => {
        async function fetchAndUpdateUsers() {
            if (!value || value.length === 0) return;
            const updatedUserList = await updateUsers(value);
            setUsers((prevUsers) => {
                if (JSON.stringify(prevUsers) !== JSON.stringify(updatedUserList)) {
                    return updatedUserList;
                }
                return prevUsers;
            });
        }

        fetchAndUpdateUsers();
    }, [value]);

    useEffect(() => {
        setSelectedUserIds(users.map((user) => user.userId));
    }, [users]);

    const onDataChanged = (data: User[]) => {
        setUsers(data);
        if (onChange) {
            onChange(data);
        }
    };

    const selector = <MSEUserSelector onUserSelect={onDataChanged} initialUsers={selectedUserIds} />;

    return (
        <PropertyTable<User>
            getColumnsConfig={getColumnsConfig}
            propertyAsRowKey="id"
            tableData={users || []}
            readOnly={false}
            elementSelector={selector}
            onDataChanged={onDataChanged}
            actions={[]}
        />
    );
};
